import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, fetchMeShared } from '../services/api';
import { writeCachedPermissions } from '../utils/permissionCache';
import { readCachedPerms, writeCachedPerms } from '../utils/bootstrapCache';

interface PermissionContextType {
  userPermissions: Record<string, string>;
  accessibleModules: string[];
  loading: boolean;
  currentUser: any;
  userProfile: any[];
  hierarchicalPermissions: Record<string, any>;
  hasPermission: (moduleName: string, operation: string, miniModuleName?: string) => boolean;
  canAccessModule: (moduleName: string) => boolean;
  getModulePermissions: (moduleName: string) => Record<string, boolean>;
  getAccessCode: (moduleName: string) => string;
  refreshPermissions: () => Promise<void>;
  initializePermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userPermissions, setUserPermissions] = useState<Record<string, string>>({});
  const [accessibleModules, setAccessibleModules] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<any[]>([]);
  const [hierarchicalPermissions, setHierarchicalPermissions] = useState<Record<string, any>>({});

  /**
   * Decode 4-digit binary access code to permissions
   * Position mapping: 0=view, 1=add, 2=edit, 3=delete (from right)
   */
  const decodeAccessCode = useCallback((codeAcces: string): Record<string, boolean> => {
    const binaryCode = String(codeAcces).padStart(4, '0');
    return {
      view: binaryCode[3] === '1',   // Position 0 from right
      add: binaryCode[2] === '1',    // Position 1 from right
      edit: binaryCode[1] === '1',   // Position 2 from right
      delete: binaryCode[0] === '1', // Position 3 from right
    };
  }, []);

  /**
   * Build hierarchical permission structure from profiles
   * Optimized for fast lookups
   */
  const buildHierarchicalPermissions = useCallback((profiles: any[]): Record<string, any> => {
    const permissions: Record<string, any> = {};

    profiles.forEach((profile) => {
      const moduleName = profile.module?.name;
      const miniModuleName = profile.mini_module?.name;
      const accessCode = profile.access?.code || '0000';

      const decodedPermissions = decodeAccessCode(accessCode);

      if (moduleName) {
        if (!permissions[moduleName]) {
          permissions[moduleName] = {};
        }

        if (miniModuleName) {
          permissions[moduleName][miniModuleName] = {
            ...decodedPermissions,
            accessCode: accessCode,
            profile: profile,
          };
        } else {
          permissions[moduleName]['_module'] = {
            ...decodedPermissions,
            accessCode: accessCode,
            profile: profile,
          };
        }
      }
    });

    return permissions;
  }, [decodeAccessCode]);

  /**
   * Load permissions from cache (instant, no network call)
   * Note: Cache key is based on user email, but we can't get it from cookies
   * So we skip cache on initial load and rely on API
   */
  const loadPermissionsFromCache = useCallback((): boolean => {
    // Apply the last-known permissions synchronously from localStorage so the
    // sidebar / route guards render instantly on refresh. fetchUserPermissions()
    // always revalidates against the server right after, so stale permissions
    // self-correct within ~1s (and the server enforces RBAC regardless).
    try {
      const cached = readCachedPerms();
      if (cached && cached.userPermissions) {
        setUserPermissions(cached.userPermissions || {});
        setAccessibleModules(cached.accessibleModules || []);
        setCurrentUser(cached.currentUser || null);
        setUserProfile(cached.userProfile || []);
        setHierarchicalPermissions(cached.hierarchicalPermissions || {});
        return true;
      }
    } catch {
      /* ignore corrupt cache */
    }
    return false;
  }, []);

  /**
   * Save permissions to cache for instant future access
   */
  const savePermissionsToCache = useCallback((username: string, permissionsData: any) => {
    try {
      if (!username) return;
      writeCachedPermissions(username, permissionsData);
    } catch (error) {
      console.error('Error saving permissions to cache:', error);
    }
  }, []);

  /**
   * Fetch user permissions from API (async, runs in background)
   */
  const fetchUserPermissions = useCallback(async () => {
    //console.log('🔄 PermissionContext: fetchUserPermissions() called');

    try {
      // Fetch user info (token is in httpOnly cookie, sent automatically)
      //console.log('📡 Fetching user info from /api/auth/users/me...');
      let user;
      try {
        // Deduped with AuthContext — shares one /me request on first load.
        user = await fetchMeShared();
        //console.log('✅ User info received:', user);
      } catch (error) {
        console.error('❌ Failed to fetch user info:', error);
        setLoading(false);
        return;
      }

      setCurrentUser(user);

      // 🔍 DEBUG: Print user information
      //console.log('═'.repeat(60));
      //console.log('👤 USER LOGGED IN');
      //console.log('═'.repeat(60));
      //console.log('📧 Email:', user.email);
      //console.log('👤 Full Name:', user.full_name);
      //console.log('🆔 User ID:', user.id);
      //console.log('🎭 Role ID:', user.role_id);
      //console.log('🏷️  Role Name:', user.user_role?.name || 'N/A');
      //console.log('✅ Active:', user.is_active);
      //console.log('─'.repeat(60));

      // Fetch user's profiles from RBAC API
      if (!user.role_id) {
        console.error('❌ SECURITY: User has no role_id assigned - NO ACCESS GRANTED');
        setUserPermissions({});
        setAccessibleModules([]);
        setUserProfile([]);
        setHierarchicalPermissions({});
        setLoading(false);
        return;
      }

      try {
        // Fetch profiles for user's role
        const profilesResponse = await api.get(`/api/profiles?role_id=${user.role_id}`);

        const profiles = profilesResponse.data;

        if (!profiles || profiles.length === 0) {
          console.warn('⚠️ No profiles found for role_id:', user.role_id, '- NO ACCESS GRANTED');
          setUserPermissions({});
          setAccessibleModules([]);
          setUserProfile([]);
          setHierarchicalPermissions({});
          setLoading(false);
          return;
        }

        //console.log('✅ Loaded', profiles.length, 'profiles for role_id:', user.role_id);

        // Build permissions structure
        const permissions: Record<string, string> = {};
        const modules: Set<string> = new Set();

        profiles.forEach((profile: any) => {
          const moduleName = profile.module?.name;
          if (moduleName) {
            modules.add(moduleName);
            if (!permissions[moduleName] || profile.access?.code > permissions[moduleName]) {
              permissions[moduleName] = profile.access?.code || '0000';
            }
          }
        });

        const profileData = buildHierarchicalPermissions(profiles);

        // 🔍 DEBUG: Print permissions
        //console.log('🔐 PERMISSIONS LOADED');
        //console.log('═'.repeat(60));
        //console.log('📊 Total Profiles:', profiles.length);
        //console.log('📦 Accessible Modules:', Array.from(modules));
        //console.log('🔑 Module Permissions:');
        Object.entries(permissions).forEach(([module, code]) => {
          const decoded = {
            view: code[3] === '1',
            add: code[2] === '1',
            edit: code[1] === '1',
            delete: code[0] === '1',
          };
          //console.log(`   ${module}: ${code} → V:${decoded.view ? '✅' : '❌'} A:${decoded.add ? '✅' : '❌'} E:${decoded.edit ? '✅' : '❌'} D:${decoded.delete ? '✅' : '❌'}`);
        });
        //console.log('─'.repeat(60));
        //console.log('📋 DETAILED PROFILES:');
        profiles.forEach((profile: any, index: number) => {
          //console.log(`   ${index + 1}. ${profile.module?.name} > ${profile.mini_module?.name}: ${profile.access?.code}`);
        });
        //console.log('═'.repeat(60));

        setUserPermissions(permissions);
        setAccessibleModules(Array.from(modules));
        setUserProfile(profiles);
        setHierarchicalPermissions(profileData);

        // Save to cache using user's email as key
        const permissionsData = {
          userPermissions: permissions,
          accessibleModules: Array.from(modules),
          currentUser: user,
          userProfile: profiles,
          hierarchicalPermissions: profileData,
        };

        savePermissionsToCache(user.email, permissionsData);
        // Also persist to localStorage so it survives a full page refresh
        // (the window-keyed cache above does not).
        writeCachedPerms(permissionsData);
        setLoading(false);
      } catch (profileError) {
        console.error('❌ Failed to fetch user profiles:', profileError);
        // SECURITY: Do NOT grant any access on error
        setUserPermissions({});
        setAccessibleModules([]);
        setUserProfile([]);
        setHierarchicalPermissions({});
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setLoading(false);
    }
  }, [buildHierarchicalPermissions, savePermissionsToCache]);

  /**
   * Initialize permissions - tries cache first, then API
   * This ensures INSTANT loading on subsequent visits
   */
  const initializePermissions = useCallback(async () => {
    //console.log('🚀 PermissionContext: initializePermissions() called');
    setLoading(true);

    // Try cache first (instant)
    //console.log('📦 Checking cache...');
    const hasCache = loadPermissionsFromCache();
    if (hasCache) {
      //console.log('✅ Loaded from cache, refreshing in background...');
      setLoading(false);
      // Refresh in background without blocking UI
      fetchUserPermissions();
      return;
    }

    //console.log('❌ No cache found, fetching from API...');
    // No cache, fetch from API
    await fetchUserPermissions();
  }, [loadPermissionsFromCache, fetchUserPermissions]);

  /**
   * Check hierarchical permission (fast O(1) lookup)
   */
  const hasHierarchicalPermission = useCallback(
    (moduleName: string, operation: string, miniModuleName?: string): boolean => {
      const modulePermissions = hierarchicalPermissions[moduleName];
      if (!modulePermissions) return false;

      let targetPermissions;

      if (miniModuleName) {
        targetPermissions = modulePermissions[miniModuleName];
      } else {
        targetPermissions = modulePermissions['_module'];

        if (!targetPermissions) {
          // Check if any sub-module has access
          const subModulePermissions = Object.keys(modulePermissions)
            .filter((key) => key !== '_module')
            .map((key) => modulePermissions[key]);

          return subModulePermissions.some((perm) => perm && perm[operation.toLowerCase()]);
        }
      }

      if (!targetPermissions) return false;
      return targetPermissions[operation.toLowerCase()] === true;
    },
    [hierarchicalPermissions]
  );

  /**
   * Check if user has permission (optimized)
   */
  const hasPermission = useCallback(
    (moduleName: string, operation: string, miniModuleName?: string): boolean => {
      if (!moduleName || !operation) return false;

      // Try hierarchical permissions first (fastest)
      if (hierarchicalPermissions && Object.keys(hierarchicalPermissions).length > 0) {
        return hasHierarchicalPermission(moduleName, operation, miniModuleName);
      }

      // Fallback to legacy permissions
      const accessCode = userPermissions[moduleName] || '0000';
      const operationMap: Record<string, number> = {
        view: 0,
        add: 1,
        edit: 2,
        delete: 3,
      };

      const position = operationMap[operation.toLowerCase()];
      if (position === undefined) return false;

      return accessCode[3 - position] === '1';
    },
    [hierarchicalPermissions, userPermissions, hasHierarchicalPermission]
  );

  /**
   * Check if user can access module
   */
  const canAccessModule = useCallback(
    (moduleName: string): boolean => {
      return accessibleModules.includes(moduleName) || hasPermission(moduleName, 'view');
    },
    [accessibleModules, hasPermission]
  );

  /**
   * Get all permissions for a module
   */
  const getModulePermissions = useCallback(
    (moduleName: string): Record<string, boolean> => {
      return {
        view: hasPermission(moduleName, 'view'),
        add: hasPermission(moduleName, 'add'),
        edit: hasPermission(moduleName, 'edit'),
        delete: hasPermission(moduleName, 'delete'),
      };
    },
    [hasPermission]
  );

  /**
   * Get access code for module
   */
  const getAccessCode = useCallback(
    (moduleName: string): string => {
      return userPermissions[moduleName] || '0000';
    },
    [userPermissions]
  );

  /**
   * Refresh permissions (clears cache and refetches)
   */
  const refreshPermissions = useCallback(async () => {
    setLoading(true);
    // Cache will be cleared and refreshed by fetchUserPermissions
    await fetchUserPermissions();
  }, [fetchUserPermissions]);

  // Initialize on mount
  useEffect(() => {
    initializePermissions();
  }, [initializePermissions]);

  const contextValue: PermissionContextType = {
    userPermissions,
    accessibleModules,
    loading,
    currentUser,
    userProfile,
    hierarchicalPermissions,
    hasPermission,
    canAccessModule,
    getModulePermissions,
    getAccessCode,
    refreshPermissions,
    initializePermissions,
  };

  return <PermissionContext.Provider value={contextValue}>{children}</PermissionContext.Provider>;
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export default PermissionContext;
